/**
 * ObjectManager — Manages lifecycle, visibility, and swap animations
 * for multiple GLB asset objects.
 *
 * Pure state + logic, no React, no R3F.
 * The R3F ObjectComponent reads ObjectManager state to render objects
 * and calls `attachScene()` once they are mounted in the scene graph.
 *
 * Follows the Manager Pattern: constructor → register → load → show/hide/swap → dispose.
 *
 * @see docs/ims-engine-api/01-architecture.md — Manager Pattern
 * @see docs/ims-engine-api/13-feature-node-swap.md — Node-swap feature
 */

import { RevealController } from './RevealController';
import { SwapAnimator } from './SwapAnimator';
import type { RevealMesh, RevealObject } from './RevealController';
import type { SwapGroup } from './SwapAnimator';
import type {
  LoadAssetConfig,
  BaseNodeConfig,
  RevealOptions,
  HideAssetOptions,
  SwapOptions,
  ManagedObjectStatus,
  ManagedObjectState,
  ExclusionGroupState,
  ObjectManagerState,
  AssetVisibilityChangedEvent,
  AssetSwappedEvent,
} from '../api/types/objectManager';
import type { LoadingPhase } from '../api/types/loading';

// ---------------------------------------------------------------------------
// Dependency interfaces (for testability — no direct Three.js coupling)
// ---------------------------------------------------------------------------

/**
 * Result of loading a GLB.
 * The `scene` is the root Object3D of the loaded GLTF.
 */
export interface LoadedGLB {
  scene: RevealObject;
}

/**
 * Options forwarded to the injected loader function.
 * Mirrors the subset of AssetLoader.LoadOptions that ObjectManager cares about.
 */
export interface LoadGLBOptions {
  /** Cache-priority phase. Defaults to `'lazy'` in the loader. */
  phase?: LoadingPhase;
}

/**
 * Injected loader function. Decouples ObjectManager from AssetLoader.
 * @returns The loaded GLB (scene root).
 */
export type LoadGLBFn = (url: string, options?: LoadGLBOptions) => Promise<LoadedGLB>;

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface InternalObject {
  config: LoadAssetConfig;
  status: ManagedObjectStatus;
  /** The loaded scene root. Set after load() completes. */
  scene: RevealObject | null;
  /**
   * Pristine loaded scene — never overwritten by attachScene().
   * ObjectComponent reads this for cloning so that R3F transforms
   * from a previous render cycle don't pollute the clone source.
   */
  loadedScene: RevealObject | null;
  /** The R3F group wrapping this object (for Y-slide). Set by attachScene(). */
  group: SwapGroup | null;
  /** Active reveal controller during fade. */
  reveal: RevealController | null;
  /** True if this object represents a node from the base model GLB. */
  isBaseNode: boolean;
  /** The Object3D.name to find in the base model scene. Only set for base nodes. */
  baseNodeName?: string;
  /**
   * When show() is called while the object is still loading, the options
   * are stored here. load() checks this after completion and automatically
   * calls show(). This ensures the old wheel stays visible until the new
   * one is ready — no "wheelless car" frames.
   */
  _pendingShowOptions?: RevealOptions | null;
  /**
   * When show() is called before the scene is mounted, reveal options are
   * stored here. attachScene() reads and applies them once the scene is ready.
   */
  _pendingRevealOptions?: RevealOptions | null;
}

interface InternalGroup {
  activeId: string | null;
  members: Set<string>;
  /**
   * Reference bbox center for this group (in body-GLB-local coordinates).
   * Set from the base node (OEM wheels) so external objects can align to it.
   */
  anchorCenter: { x: number; y: number; z: number } | null;
  /**
   * When show() needs to hide the current active member but the NEW member
   * requires React mounting (non-base-node GLB), the hide is deferred until
   * the new member's `attachScene()` fires. This ensures the old member
   * stays fully visible until the replacement is rendered — no "wheelless"
   * frames.
   *
   * Cleared by `attachScene()` after executing the deferred hide.
   */
  _pendingGroupHide: { id: string; animated: boolean } | null;
}

// ---------------------------------------------------------------------------
// ObjectManager
// ---------------------------------------------------------------------------

export class ObjectManager {
  private _objects = new Map<string, InternalObject>();
  private _groups = new Map<string, InternalGroup>();
  private _swapAnimator: SwapAnimator | null = null;
  private _disposed = false;
  private _loadFn: LoadGLBFn;
  private _changeListeners = new Set<() => void>();
  private _visibilityListeners = new Set<(event: AssetVisibilityChangedEvent) => void>();

  // -----------------------------------------------------------------------
  // Callbacks (Manager Pattern: mutable callback properties)
  // -----------------------------------------------------------------------

  /** Called on any state change — triggers store/UI update. */
  onChange: (() => void) | null = null;
  /** Called when a swap transition completes. */
  onSwapped: ((event: AssetSwappedEvent) => void) | null = null;
  /**
   * Called when an external GLB's scene is mounted in the R3F tree.
   * Allows other systems (e.g. WheelManager) to scan the new scene for
   * nodes they need to track (e.g. steering bones in rim GLBs).
   */
  onObjectMounted?: (id: string, scene: RevealObject) => void;

  constructor(loadFn: LoadGLBFn) {
    this._loadFn = loadFn;
  }

  // -----------------------------------------------------------------------
  // Multi-listener change subscription
  // -----------------------------------------------------------------------

  /**
   * Subscribe to state changes. Supports multiple simultaneous listeners
   * (unlike the single `onChange` callback).
   *
   * Used by ObjectComponent instances (world + body scopes) that both need
   * change notifications from the same ObjectManager.
   *
   * @returns Unsubscribe function.
   */
  addChangeListener(fn: () => void): () => void {
    this._changeListeners.add(fn);
    return () => {
      this._changeListeners.delete(fn);
    };
  }

  /**
   * Subscribe to asset visibility changes (object shown or hidden).
   * Supports multiple simultaneous listeners — consistent with `addChangeListener`.
   *
   * Used by `createEngine` to bridge into the EventEmitter-based public API
   * (`engine.on('assetVisibilityChanged', ...)`), and by any other consumer
   * that needs fine-grained visibility notifications.
   *
   * @returns Unsubscribe function.
   */
  addVisibilityListener(fn: (event: AssetVisibilityChangedEvent) => void): () => void {
    this._visibilityListeners.add(fn);
    return () => {
      this._visibilityListeners.delete(fn);
    };
  }

  // -----------------------------------------------------------------------
  // Group anchors (for aligning external objects with base nodes)
  // -----------------------------------------------------------------------

  /**
   * Store a reference bbox center for an exclusion group.
   *
   * Typically computed from the base node (OEM wheels) in the original
   * GLTF scene so external replacement objects can be aligned to the
   * same position.
   *
   * Coordinates are in the body-GLB's native coordinate space (no R3F
   * transforms applied).
   */
  setGroupAnchor(groupId: string, center: { x: number; y: number; z: number }): void {
    const grp = this._groups.get(groupId);
    if (grp) grp.anchorCenter = center;
  }

  /**
   * Get the reference bbox center for an exclusion group.
   * Returns `null` if no anchor has been set.
   */
  getGroupAnchor(groupId: string): { x: number; y: number; z: number } | null {
    return this._groups.get(groupId)?.anchorCenter ?? null;
  }

  // -----------------------------------------------------------------------
  // Registration
  // -----------------------------------------------------------------------

  /**
   * Register an asset for management. Does NOT load or show.
   * Must be called before `load()`, `show()`, etc.
   *
   * @throws Error if an object with the same `id` is already registered.
   */
  register(config: LoadAssetConfig): void {
    if (this._disposed) return;
    if (this._objects.has(config.id)) {
      throw new Error(`[ObjectManager] Object "${config.id}" already registered`);
    }

    this._objects.set(config.id, {
      config,
      status: 'pending',
      scene: null,
      loadedScene: null,
      group: null,
      reveal: null,
      isBaseNode: false,
    });

    // Add to exclusion group
    if (config.group) {
      let grp = this._groups.get(config.group);
      if (!grp) {
        grp = { activeId: null, members: new Set(), anchorCenter: null, _pendingGroupHide: null };
        this._groups.set(config.group, grp);
      }
      grp.members.add(config.id);
    }

    this._notify();
  }

  // -----------------------------------------------------------------------
  // Base node registration
  // -----------------------------------------------------------------------

  /**
   * Register a node from the base model GLB as a member of an exclusion group.
   *
   * This enables seamless animated swaps between the original part (e.g. OEM
   * wheels) and replacement GLBs (e.g. sport wheels) using the same
   * `swap()` / `show()` / `hide()` API with identical animations.
   *
   * The base node's actual scene reference is wired up later via
   * `setModelScene()` (called from `onMounted`).
   *
   * @throws Error if an object with the same `id` is already registered.
   */
  registerBaseNode(config: BaseNodeConfig): void {
    if (this._disposed) return;
    if (this._objects.has(config.id)) {
      throw new Error(`[ObjectManager] Object "${config.id}" already registered`);
    }

    // Create a synthetic LoadAssetConfig (no URL for base nodes)
    const syntheticConfig: LoadAssetConfig = {
      id: config.id,
      url: '',
      group: config.group,
    };

    this._objects.set(config.id, {
      config: syntheticConfig,
      status: 'pending', // waiting for setModelScene
      scene: null,
      loadedScene: null,
      group: null,
      reveal: null,
      isBaseNode: true,
      baseNodeName: config.nodeName,
    });

    // Add to exclusion group
    let grp = this._groups.get(config.group);
    if (!grp) {
      grp = { activeId: null, members: new Set(), anchorCenter: null, _pendingGroupHide: null };
      this._groups.set(config.group, grp);
    }
    grp.members.add(config.id);

    this._notify();
  }

  // -----------------------------------------------------------------------
  // Model scene connection (for base nodes)
  // -----------------------------------------------------------------------

  /**
   * Connect base nodes to the rendered model scene graph.
   *
   * Called once from `onMounted` after ModelComponent clones and mounts the
   * GLB scene. Traverses the scene to find nodes matching registered base
   * node names, wires up scene/group refs, and sets initial visibility.
   *
   * Base nodes whose group already has an active member are hidden; otherwise
   * the base node becomes the default active member.
   */
  setModelScene(scene: RevealObject): void {
    if (this._disposed) return;

    for (const [id, obj] of this._objects) {
      if (!obj.isBaseNode || !obj.baseNodeName) continue;

      // Find the node by name in the scene graph
      let found: RevealObject | null = null;
      scene.traverse((child) => {
        if ((child as RevealMesh & { name?: string }).name === obj.baseNodeName) {
          found = child as unknown as RevealObject;
        }
      });

      if (!found) {
        console.warn(`[ObjectManager] Base node "${obj.baseNodeName}" not found in model scene`);
        continue;
      }

      // Wire references — the node serves as both scene (for RevealController)
      // and group (for SwapAnimator Y-slide via position.y).
      const foundNode = found as RevealObject;
      obj.scene = foundNode;
      obj.group = foundNode as unknown as SwapGroup;

      // Check if group already has an active member
      const grp = this._groups.get(obj.config.group!);
      if (grp && grp.activeId !== null) {
        // Another member is already active — hide this base node
        obj.status = 'hidden';
        foundNode.visible = false;
      } else {
        // No active member — this base node is the default visible part
        obj.status = 'visible';
        if (grp) grp.activeId = id;
      }
    }

    this._notify();
  }

  // -----------------------------------------------------------------------
  // Loading
  // -----------------------------------------------------------------------

  /**
   * Load the GLB asset from network (or cache).
   * Transitions: pending → loading → loaded.
   * If `config.showOnLoad` is true, automatically calls `show()` after load.
   *
   * @returns Promise that resolves when load + optional show completes.
   */
  async load(id: string): Promise<void> {
    const obj = this._require(id);
    if (obj.status !== 'pending') return; // idempotent
    if (obj.isBaseNode) return; // Base nodes get their scene from setModelScene

    obj.status = 'loading';
    this._notify();

    try {
      const result = await this._loadFn(obj.config.url, {
        phase: obj.config.phase,
      });
      if (this._disposed) return; // engine may have been disposed during load

      obj.scene = result.scene;
      obj.loadedScene = result.scene;
      obj.status = 'loaded';
      this._notify();

      // If show() was called while loading, execute it now.
      if (obj._pendingShowOptions !== undefined) {
        const opts = obj._pendingShowOptions ?? undefined;
        delete obj._pendingShowOptions;
        this.show(id, opts);
      } else if (obj.config.showOnLoad) {
        this.show(id, obj.config.revealOptions);
      }
    } catch (err) {
      if (!this._disposed) {
        obj.status = 'pending'; // revert for retry
        this._notify();
      }
      throw err;
    }
  }

  // -----------------------------------------------------------------------
  // Scene attachment (called by ObjectComponent when mounted in R3F)
  // -----------------------------------------------------------------------

  /**
   * Called by ObjectComponent once the object's Three.js scene is in the
   * R3F tree. This provides the live scene and group references needed
   * for RevealController and SwapAnimator.
   *
   * If the object is in 'revealing' status (show was called before mount),
   * this will start the fade-in animation.
   *
   * Also executes any deferred exclusion-group hide that was postponed by
   * `show()` until this object was mounted (prevents "wheelless" frames).
   */
  attachScene(id: string, scene: RevealObject, group: SwapGroup): void {
    const obj = this._objects.get(id);
    if (!obj) return;

    obj.scene = scene;
    obj.group = group;

    // Execute any deferred exclusion-group hide now that the new member
    // is mounted and visible in the Three.js scene graph.
    if (obj.config.group) {
      const grp = this._groups.get(obj.config.group);
      if (grp?._pendingGroupHide) {
        const { id: hideId, animated } = grp._pendingGroupHide;
        grp._pendingGroupHide = null;
        this.hide(hideId, animated ? undefined : { animated: false });
      }
    }

    // If show() was called before the scene was mounted, start the reveal now
    if (obj.status === 'revealing' && !obj.reveal) {
      this._startRevealIn(obj, id);
    }

    // Notify external systems (e.g. WheelManager) that a new scene is live
    if (!obj.isBaseNode) {
      this.onObjectMounted?.(id, scene);
    }
  }

  // -----------------------------------------------------------------------
  // Visibility — show
  // -----------------------------------------------------------------------

  /**
   * Make an object visible with optional fade animation.
   * If the object's scene is not yet mounted (no `attachScene` call),
   * it will be queued for reveal when mounted.
   *
   * If the object is in an exclusion group, hides the currently active member.
   */
  show(id: string, options?: RevealOptions): void {
    if (this._disposed) return;
    const obj = this._require(id);

    // If the object is still loading, queue the show for after load completes.
    // The old group member stays visible until then — no "wheelless" frames.
    if (obj.status === 'loading') {
      obj._pendingShowOptions = options ?? null;
      return;
    }

    // Only show from loaded or hidden state
    if (obj.status !== 'loaded' && obj.status !== 'hidden') return;

    // Clear any pending show (we're executing it now)
    delete obj._pendingShowOptions;

    // Handle exclusion group — hide current active
    if (obj.config.group) {
      const grp = this._groups.get(obj.config.group);
      if (grp && grp.activeId && grp.activeId !== id) {
        const oldId = grp.activeId;
        const hideAnimated = options?.animated !== false;

        if (obj.isBaseNode) {
          // New member is a base node — its Object3D becomes visible instantly
          // (via visible=true below), so hiding the old member immediately is safe.
          this.hide(oldId, hideAnimated ? undefined : { animated: false });
        } else {
          // New member is an external GLB that needs React mounting before it
          // appears on screen. Defer the old member's hide until the new
          // member's attachScene() fires — this guarantees the old wheels stay
          // fully visible until the replacement is rendered.
          grp._pendingGroupHide = { id: oldId, animated: hideAnimated };
        }
      }
      if (grp) grp.activeId = id;
    }

    // Store reveal options for deferred start (if scene not mounted yet)
    obj.status = 'revealing';
    this._notify();

    // For base nodes, make the Object3D renderable before fade-in.
    // RevealController will start at opacity 0, so no visual flash.
    if (obj.isBaseNode && obj.scene) {
      obj.scene.visible = true;
    }

    if (!obj.scene) {
      // Scene not mounted yet — ObjectComponent will render it,
      // then attachScene() will start the actual fade.
      // Store options so attachScene can use them.
      obj._pendingRevealOptions = options;
      return;
    }

    this._startRevealIn(obj, id, options);
  }

  // -----------------------------------------------------------------------
  // Visibility — hide
  // -----------------------------------------------------------------------

  /**
   * Hide an object with optional fade animation.
   *
   * @param options.dispose — Also remove from cache (free GPU memory). Default: false
   */
  hide(id: string, options?: HideAssetOptions): void {
    if (this._disposed) return;
    const obj = this._require(id);

    if (obj.status !== 'visible' && obj.status !== 'revealing') return;

    // Cancel any running reveal
    if (obj.reveal) {
      obj.reveal.dispose();
      obj.reveal = null;
    }

    // Update exclusion group
    if (obj.config.group) {
      const grp = this._groups.get(obj.config.group);
      if (grp && grp.activeId === id) {
        grp.activeId = null;
      }
    }

    if (!obj.scene || options?.animated === false) {
      // Instant hide
      obj.status = 'hidden';
      this._setBaseNodeHidden(obj);
      // Clear mounted-scene refs for non-base-node objects so a subsequent
      // show() correctly defers the exclusion-group hide to attachScene().
      // (base nodes keep their refs — they live in the model scene graph.)
      if (!obj.isBaseNode) {
        obj.scene = null;
        obj.group = null;
      }
      this._notify();
      this._notifyVisibility({ id, visible: false });
      options?.onComplete?.();
      return;
    }

    // Animated fade out
    obj.status = 'hiding';
    this._notify();

    const reveal = new RevealController();
    reveal.attach(obj.scene);
    reveal.fadeOut({
      duration: options?.duration,
      easing: options?.easing,
      onComplete: () => {
        reveal.dispose();
        obj.reveal = null;
        obj.status = 'hidden';
        this._setBaseNodeHidden(obj);
        if (!obj.isBaseNode) {
          obj.scene = null;
          obj.group = null;
        }
        this._notify();
        this._notifyVisibility({ id, visible: false });
        options?.onComplete?.();
      },
    });
    obj.reveal = reveal;
  }

  // -----------------------------------------------------------------------
  // Swap
  // -----------------------------------------------------------------------

  /**
   * Swap the active object in an exclusion group to a new one with animation.
   *
   * The old object fades out + slides down, the new one fades in + slides up.
   * If no old object is active, performs a simple reveal.
   *
   * @param groupId — Exclusion group ID.
   * @param newId   — ID of the object to swap to (must be a group member).
   * @param options — Swap animation options.
   */
  swap(groupId: string, newId: string, options?: SwapOptions): void {
    if (this._disposed) return;

    const grp = this._groups.get(groupId);
    if (!grp) throw new Error(`[ObjectManager] Group "${groupId}" not found`);
    if (!grp.members.has(newId)) {
      throw new Error(`[ObjectManager] "${newId}" is not a member of group "${groupId}"`);
    }

    const oldId = grp.activeId;
    if (oldId === newId) return; // already active

    const newObj = this._require(newId);
    if (newObj.status !== 'loaded' && newObj.status !== 'hidden') {
      throw new Error(`[ObjectManager] Cannot swap to "${newId}" — status is "${newObj.status}"`);
    }

    const oldObj = oldId ? this._objects.get(oldId) : undefined;

    // Cancel any existing swap
    if (this._swapAnimator) {
      this._swapAnimator.dispose();
      this._swapAnimator = null;
    }

    // Cancel existing reveals on both sides
    if (oldObj?.reveal) {
      oldObj.reveal.dispose();
      oldObj.reveal = null;
    }
    if (newObj.reveal) {
      newObj.reveal.dispose();
      newObj.reveal = null;
    }

    // Update group
    grp.activeId = newId;

    // State transitions
    if (oldObj) oldObj.status = 'hiding';
    newObj.status = 'revealing';
    this._notify();

    // Check if both scenes are mounted (needed for animated swap)
    const canAnimate =
      newObj.scene !== null &&
      newObj.group !== null &&
      (!oldObj || (oldObj.scene !== null && oldObj.group !== null));

    if (!canAnimate) {
      // Scenes not mounted — fall back to instant state change
      // ObjectComponent will handle rendering
      if (oldObj) {
        oldObj.status = 'hidden';
        this._setBaseNodeHidden(oldObj);
      }
      // For new base nodes, make visible before deferred reveal
      if (newObj.isBaseNode && newObj.scene) {
        newObj.scene.visible = true;
      }
      // newObj stays 'revealing' — attachScene will handle fade in
      this._notify();
      return;
    }

    // For base nodes being swapped IN, make the Object3D renderable.
    // SwapAnimator will set opacity 0 immediately, so no visual flash.
    if (newObj.isBaseNode && newObj.scene) {
      newObj.scene.visible = true;
    }

    // Start animated swap
    const animator = new SwapAnimator();
    animator.start(oldObj?.scene ?? null, oldObj?.group ?? null, newObj.scene!, newObj.group!, {
      duration: options?.duration ?? 800,
      slideDistance: options?.slideDistance,
      overlap: options?.overlap,
      onComplete: () => {
        if (oldObj) {
          oldObj.status = 'hidden';
          this._setBaseNodeHidden(oldObj);
        }
        newObj.status = 'visible';
        this._swapAnimator = null;
        this._notify();
        this.onSwapped?.({ group: groupId, oldId: oldId ?? null, newId });
        options?.onComplete?.();
      },
    });
    this._swapAnimator = animator;
  }

  // -----------------------------------------------------------------------
  // Per-frame update
  // -----------------------------------------------------------------------

  /**
   * Update all active animations. Called from useFrame in ObjectComponent.
   * @param deltaMs — Time since last frame in milliseconds.
   */
  tick(deltaMs: number): void {
    if (this._disposed) return;
    if (!this.isAnimating) return; // Short-circuit: nothing to animate

    // Update individual reveals
    for (const obj of this._objects.values()) {
      if (obj.reveal) {
        const running = obj.reveal.tick(deltaMs);
        if (!running) {
          // Animation finished — callback already handled cleanup
        }
      }
    }

    // Update swap animator
    if (this._swapAnimator) {
      this._swapAnimator.tick(deltaMs);
    }
  }

  // -----------------------------------------------------------------------
  // State accessors
  // -----------------------------------------------------------------------

  /** Get full serialisable state (for store/UI). */
  getState(): ObjectManagerState {
    const objects: ManagedObjectState[] = [];
    for (const [id, obj] of this._objects) {
      objects.push(this._toPublicState(id, obj));
    }

    const groups: ExclusionGroupState[] = [];
    for (const [id, grp] of this._groups) {
      groups.push({
        id,
        activeId: grp.activeId,
        members: [...grp.members],
      });
    }

    return { objects, groups };
  }

  /** Get a single object's state. */
  getObjectState(id: string): ManagedObjectState | undefined {
    const obj = this._objects.get(id);
    if (!obj) return undefined;
    return this._toPublicState(id, obj);
  }

  /**
   * Get the pristine loaded scene and load config for an object.
   * Used by ObjectComponent to clone scenes for rendering without breaking
   * encapsulation via `_objects` access.
   */
  getLoadedScene(
    id: string,
  ): { loadedScene: RevealObject | null; config: LoadAssetConfig } | undefined {
    const obj = this._objects.get(id);
    if (!obj) return undefined;
    return { loadedScene: obj.loadedScene, config: obj.config };
  }

  /** Check whether any animations are currently running. */
  get isAnimating(): boolean {
    if (this._swapAnimator?.isAnimating) return true;
    for (const obj of this._objects.values()) {
      if (obj.reveal?.isAnimating) return true;
    }
    return false;
  }

  /** Number of registered objects. */
  get size(): number {
    return this._objects.size;
  }

  // -----------------------------------------------------------------------
  // Dispose
  // -----------------------------------------------------------------------

  /** Dispose all state and animation controllers. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Dispose all active reveals
    for (const obj of this._objects.values()) {
      obj.reveal?.dispose();
      obj.reveal = null;
    }

    // Dispose swap animator
    this._swapAnimator?.dispose();
    this._swapAnimator = null;

    this._objects.clear();
    this._groups.clear();
    this._changeListeners.clear();
    this._visibilityListeners.clear();
    this.onChange = null;
    this.onSwapped = null;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /** Get an object or throw. */
  private _require(id: string): InternalObject {
    const obj = this._objects.get(id);
    if (!obj) throw new Error(`[ObjectManager] Object "${id}" not found`);
    return obj;
  }

  /** Convert internal state to public ManagedObjectState. */
  private _toPublicState(id: string, obj: InternalObject): ManagedObjectState {
    return {
      id,
      url: obj.config.url,
      group: obj.config.group ?? null,
      status: obj.status,
      visible: obj.status === 'visible' || obj.status === 'revealing',
      parentRelative: obj.config.parentRelative ?? true,
      isBaseNode: obj.isBaseNode,
    };
  }

  /**
   * For base nodes, set the Object3D.visible = false.
   * This prevents rendering of the hidden node that remains in ModelComponent's
   * scene graph. For regular managed objects this is a no-op — ObjectComponent
   * handles mounting/unmounting.
   */
  private _setBaseNodeHidden(obj: InternalObject): void {
    if (obj.isBaseNode && obj.scene) {
      obj.scene.visible = false;
    }
  }

  /** Start a fade-in reveal on an object that has its scene attached. */
  private _startRevealIn(obj: InternalObject, id: string, options?: RevealOptions): void {
    // Check for pending options stored by show()
    const opts = options ?? obj._pendingRevealOptions;
    delete obj._pendingRevealOptions;

    if (opts?.animated === false) {
      // Instant show
      obj.status = 'visible';
      this._notify();
      this._notifyVisibility({ id, visible: true });
      opts?.onComplete?.();
      return;
    }

    const reveal = new RevealController();
    reveal.attach(obj.scene!);
    reveal.setImmediate(0); // start invisible

    reveal.fadeIn({
      duration: opts?.duration,
      easing: opts?.easing,
      onComplete: () => {
        reveal.dispose();
        obj.reveal = null;
        obj.status = 'visible';
        this._notify();
        this._notifyVisibility({ id, visible: true });
        opts?.onComplete?.();
      },
    });
    obj.reveal = reveal;
  }

  /** Trigger onChange + all registered change listeners. */
  private _notify(): void {
    this.onChange?.();
    for (const fn of this._changeListeners) fn();
  }

  /** Notify all visibility listeners. */
  private _notifyVisibility(event: AssetVisibilityChangedEvent): void {
    for (const fn of this._visibilityListeners) fn(event);
  }
}
